/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/criteo
 */
/**
 * @description Implements functionality for Criteo header bidding
 * @extends _bidbase
 * @requires module:utils
 */
class _criteobid extends bidbase {
  constructor() {
    super('criteo');
    this._mandatoryConfigs = ['script'];
    this._posId = 'zoneid';
  }

  /**
   * Default implementation of _externalScriptCheck. If required bidder implementation should override this.
   * @returns {initStatus} - Status
   * @access private
   */
  _externalScriptCheck(): initStatus {
    if (u.isObject(window.Criteo) && u.isFunction(window.Criteo.RequestBids)) {
      return u.F_INITED;
    }
    return u.F_FAILED;
  }

  /**
   * Process bid response. Response Format:
   * res[slot].cpm - ecpm
   * res[slot].impressionId - pos
   * res[slot].zoneid - per position unique id
   * @param {Array<string>} posList - array of positions
   * @param {Object} res - Criteo response object
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    res: Object,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!u.isArray(res)) {
      return ret;
    }
    res.forEach(slot => {
      if (
        slot &&
        slot.impressionId &&
        this.getPosKey(slot.impressionId) === String(slot.zoneId) &&
        slot.cpm &&
        slot.id
      ) {
        this._handleBidPosData(
          ret,
          slot.impressionId,
          `${slot.cpm.toFixed(2)}`,
          slot.id
        );
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - HB position list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    window.criteo_pubtag.standaloneBidder.bids = {};
    let adUnits = {};
    /* eslint-disable arrow-body-style */
    adUnits.placements = posList.map(v => {
      return {
        zoneid: this.getPosKey(v),
        slotid: v
      };
    });
    /* eslint-enable arrow-body-style */

    return new Promise(resolve => {
      window.Criteo.events.push(() => {
        window.Criteo.RequestBids(
          adUnits,
          () => {
            resolve(
              this._processBidResponse(posList, window.Criteo.GetBids(), ret)
            );
          },
          this._config.timeout
        );
      });
    });
  }

  /**
   * render ad for this partner
   * @param {window} win - window object
   * @param {window.document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - DOM ID
   * @returns {undefined}
   */
  renderAd(win: window, doc: window.document, pos: string, id: string) {
    try {
      window.Criteo.RenderAd(id, doc);
    } catch (error) {
      u.logError(`${this._name} ${pos} render ad failure ${error.message}`);
    }
  }
}

//add bidder to bidmanager
let criteobid = new _criteobid();
bm.addBidder('criteo', criteobid);
export default criteobid;
